/**
 * dashboardConfig.js — 149-cn (PROMPT 16-P0)
 *
 * Widget registry + per-view (Daily/Weekly/Monthly) storage + role-default resolver
 * + PERMISSION-AWARE filter.
 *
 * Prinsip enforcement (mirror backend `_has_perm`):
 *   1. BOD Lv1 super-admin → semua widget visible
 *   2. Widget wajib punya `permission: {feature, perm}` — sumber tunggal.
 *      Widget hanya di-render KALAU canPerm(user, feature, perm) === true.
 *   3. Widget yang di-filter TIDAK muncul di Dashboard, TIDAK muncul di
 *      dialog "Atur Widget", TIDAK melakukan fetch (Widget wrapper mengembalikan
 *      null → children tidak di-mount).
 *   4. Saved layout lama TIDAK dihapus — kalau permission dikembalikan, widget
 *      otomatis muncul kembali di urutan lama.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useAccess } from "@/lib/access";

// Widget registry
export const DASHBOARD_WIDGETS = [
  // Global (semua view). Widget "shell" tanpa permission requirement.
  { id: "announcements",       label: "Pengumuman",                          views: ["daily", "weekly", "monthly"], permission: { feature: "announcements", perm: "view" } },
  { id: "greeting",            label: "Salam Pembuka",                       views: ["daily", "weekly", "monthly"] },  // no perm — semua boleh
  { id: "project_filter",      label: "Filter Project",                      views: ["daily", "weekly", "monthly"], permission: { feature: "projects", perm: "view" } },

  // Daily
  { id: "pending_actions",     label: "Aksi Menunggu Anda",                  views: ["daily"], permission: { feature: "tasks", perm: "view" }, drillDown: "/tasks" },
  { id: "on_leave_today",      label: "Karyawan Cuti Hari Ini",              views: ["daily"], permission: { feature: "leaves", perm: "view" }, drillDown: "/attendance?tab=cuti_izin" },
  { id: "agenda_today",        label: "Agenda & Reminder Hari Ini + Overdue",views: ["daily"], permission: { feature: "reminders", perm: "view" }, drillDown: "/info-agenda?tab=reminder", isNew: true },
  { id: "stat_cards",          label: "Statistik Tugas (4 kartu)",           views: ["daily"], permission: { feature: "tasks", perm: "view" } },
  { id: "task_lists",          label: "Daftar Tugas Saya & Delegasi",        views: ["daily"], permission: { feature: "tasks", perm: "view" }, drillDown: "/tasks" },

  // Daily/Weekly (finansial ringan)
  { id: "collection_widget",   label: "Collection · Overdue & Janji Bayar",  views: ["daily", "weekly"], permission: { feature: "collection", perm: "view" }, drillDown: "/collection" },
  // 149-cw-r105 (2026-08-28) — Widget baru: overdue janji bayar & follow-up needed.
  { id: "overdue_promise",     label: "Follow-Up Client · Janji Bayar & FU", views: ["daily", "weekly"], permission: { feature: "collection", perm: "view" }, drillDown: "/projects?tab=ar", isNew: true },
  { id: "backlog_realisasi",   label: "Backlog Realisasi Petty Cash",        views: ["daily", "weekly"], permission: { feature: "petty_cash", perm: "view" } },
  { id: "staff_cash_recap",    label: "Staff Cash Recap",                    views: ["daily", "weekly"], permission: { feature: "petty_cash", perm: "view" } },
  // 2026-10-19 (user request "KLAIM RETENSI MENUNGGU PEMBAYARAN"): widget lama
  // `retention_claims` (total+count) digantikan widget status 6 angka clickable.
  // Id lama dihapus dari registry → otomatis hilang dari layout tersimpan user
  // (sanitize() memfilter id yang tidak lagi eligible).
  { id: "retention_claim_status", label: "Klaim Retensi · Status Pembayaran",  views: ["daily", "weekly", "monthly"], permission: { feature: "collection", perm: "view" }, drillDown: "/collection?status=retention_claim", isNew: true },

  // Weekly & Monthly (trend)
  { id: "filter_summary",      label: "Ringkasan Filter (trend MoM/QoQ/YTD)",views: ["weekly", "monthly"], permission: { feature: "projects", perm: "view" } },

  // Monthly (financial year & month)
  { id: "financial_by_year",   label: "Financial Overview · By Year",        views: ["monthly"], permission: { feature: "finance_report", perm: "view" }, drillDown: "/finance-report" },
  { id: "financial_by_month",  label: "Financial Overview · By Month",       views: ["monthly"], permission: { feature: "finance_report", perm: "view" }, drillDown: "/finance-report" },
  { id: "performance_summary", label: "Performance Summary",                 views: ["monthly"], permission: { feature: "performance", perm: "view" }, drillDown: "/performance" },
];

export const DASHBOARD_VIEWS = [
  { id: "daily",   label: "Harian",   short: "Hari Ini"   },
  { id: "weekly",  label: "Mingguan",  short: "Minggu Ini" },
  { id: "monthly", label: "Bulanan", short: "Bulan Ini"  },
];

const ROLE_DEFAULTS = {
  bod: {
    daily:   ["announcements","greeting","pending_actions","agenda_today","collection_widget","overdue_promise","stat_cards","task_lists","on_leave_today","project_filter","backlog_realisasi","retention_claim_status","staff_cash_recap"],
    weekly:  ["announcements","greeting","project_filter","filter_summary","collection_widget","overdue_promise","retention_claim_status","backlog_realisasi","staff_cash_recap"],
    monthly: ["announcements","greeting","project_filter","filter_summary","financial_by_year","financial_by_month","retention_claim_status","performance_summary"],
  },
  fat: {
    daily:   ["announcements","greeting","pending_actions","agenda_today","collection_widget","overdue_promise","backlog_realisasi","staff_cash_recap","stat_cards","task_lists","project_filter","retention_claim_status","on_leave_today"],
    weekly:  ["announcements","greeting","project_filter","filter_summary","collection_widget","overdue_promise","backlog_realisasi","staff_cash_recap","retention_claim_status"],
    monthly: ["announcements","greeting","project_filter","filter_summary","financial_by_year","financial_by_month","retention_claim_status"],
  },
  project: {
    daily:   ["announcements","greeting","project_filter","pending_actions","agenda_today","stat_cards","task_lists","on_leave_today"],
    weekly:  ["announcements","greeting","project_filter","stat_cards","task_lists"],
    monthly: ["announcements","greeting","project_filter","performance_summary"],
  },
  hr: {
    daily:   ["announcements","greeting","on_leave_today","pending_actions","agenda_today","stat_cards","task_lists","project_filter"],
    weekly:  ["announcements","greeting","on_leave_today","project_filter","filter_summary"],
    monthly: ["announcements","greeting","on_leave_today","project_filter","filter_summary","performance_summary"],
  },
  // 149-co: Procurement Asset Logistic — fokus di Asset + Task + Petty Cash.
  // Widget baru akan otomatis hidden kalau permission modulnya OFF.
  pal: {
    daily:   ["announcements","greeting","pending_actions","agenda_today","staff_cash_recap","backlog_realisasi","task_lists","project_filter","on_leave_today"],
    weekly:  ["announcements","greeting","project_filter","staff_cash_recap","backlog_realisasi","task_lists"],
    monthly: ["announcements","greeting","project_filter","performance_summary"],
  },
};

export const resolveUserRole = (user) => {
  if (!user) return "project";
  const div = (user.division || "").toUpperCase();
  if (div === "BOD") return "bod";
  if (div === "FAT") return "fat";
  if (div === "HR" || div === "GA" || div === "HRGA") return "hr";
  // 149-co (PROMPT 16-P0-R1): Procurement Asset Logistic dikenali terpisah,
  // TIDAK diklasifikasikan sebagai PROJECT.
  if (div === "PROCUREMENT ASSET LOGISTIC" || div === "PROCUREMENT" || div === "ASSET LOGISTIC" || div === "PAL") return "pal";
  return "project";
};

const buildDefaultView = (role, view) => {
  const preferredOrder = (ROLE_DEFAULTS[role] || ROLE_DEFAULTS.project)[view] || [];
  const eligibleIds = DASHBOARD_WIDGETS.filter((w) => w.views.includes(view)).map((w) => w.id);
  const ordered = preferredOrder.filter((id) => eligibleIds.includes(id));
  const rest = eligibleIds.filter((id) => !ordered.includes(id));
  return { order: [...ordered, ...rest], hidden: {} };
};

const STORAGE_KEY = "ens_dashboard_config_v2";
const OLD_KEY_V1  = "ens_dashboard_config_v1";
const EVENT = "ens:dashboard-config-changed";

const defaultConfig = (role) => ({
  _schema: 2,
  activeView: "daily",
  views: {
    daily:   buildDefaultView(role, "daily"),
    weekly:  buildDefaultView(role, "weekly"),
    monthly: buildDefaultView(role, "monthly"),
  },
});

const migrateV1 = (v1, role) => {
  const base = defaultConfig(role);
  const knownIds = new Set(DASHBOARD_WIDGETS.filter((w) => w.views.includes("daily")).map((w) => w.id));
  const v1Order = (v1.order || []).filter((id) => knownIds.has(id));
  const missing = [...knownIds].filter((id) => !v1Order.includes(id));
  return {
    ...base,
    views: {
      ...base.views,
      daily: { order: [...v1Order, ...missing], hidden: { ...(v1.hidden || {}) } },
    },
  };
};

const loadConfigSync = (role) => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && parsed._schema === 2) return sanitize(parsed, role);
    }
    const v1Raw = localStorage.getItem(OLD_KEY_V1);
    if (v1Raw) {
      const v1 = JSON.parse(v1Raw);
      const migrated = migrateV1(v1, role);
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(migrated)); } catch {}
      return migrated;
    }
  } catch {}
  return defaultConfig(role);
};

const sanitize = (cfg, role) => {
  const out = { _schema: 2, activeView: cfg.activeView || "daily", views: {} };
  for (const view of ["daily", "weekly", "monthly"]) {
    const cur = cfg.views?.[view] || {};
    const eligibleIds = DASHBOARD_WIDGETS.filter((w) => w.views.includes(view)).map((w) => w.id);
    const savedOrder = (cur.order || []).filter((id) => eligibleIds.includes(id));
    const missing = eligibleIds.filter((id) => !savedOrder.includes(id));
    out.views[view] = { order: [...savedOrder, ...missing], hidden: { ...(cur.hidden || {}) } };
    if (savedOrder.length === 0 && missing.length === 0) {
      out.views[view] = buildDefaultView(role, view);
    }
  }
  return out;
};

const saveConfigLocal = (cfg) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(cfg)); } catch {}
};

const BACKEND_KEY = "layout";
let _backendFetchPromise = null;
const fetchFromBackend = async () => {
  if (_backendFetchPromise) return _backendFetchPromise;
  _backendFetchPromise = api.get(`/user-prefs/dashboard/${BACKEND_KEY}`).then((r) => r.data).catch(() => null);
  return _backendFetchPromise;
};
const saveToBackend = (cfg) => {
  api.put(`/user-prefs/dashboard/${BACKEND_KEY}`, cfg).catch(() => {});
};

// 149-cn: exported helper — dipakai hook + DashboardPage + Widget wrapper.
export const hasWidgetPermission = (w, canPerm, user) => {
  if (!w?.permission) return true; // widget tanpa gate = universal
  return canPerm(user, w.permission.feature, w.permission.perm);
};

// -----------------------------------------------------------------------------
export function useDashboardConfig({ user, syncBackend = true } = {}) {
  const role = resolveUserRole(user);
  const { canPerm } = useAccess();
  const [config, setConfig] = useState(() => loadConfigSync(role));
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!syncBackend || !user?.id) return;
    let cancelled = false;
    fetchFromBackend().then((remote) => {
      if (cancelled || !remote || !remote._schema) return;
      const merged = sanitize(remote, role);
      setConfig(merged);
      saveConfigLocal(merged);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, syncBackend]);

  useEffect(() => {
    const handler = () => setConfig(loadConfigSync(role));
    window.addEventListener(EVENT, handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener(EVENT, handler);
      window.removeEventListener("storage", handler);
    };
  }, [role]);

  const update = useCallback((mutator) => {
    setConfig((prev) => {
      const next = mutator(prev);
      saveConfigLocal(next);
      window.dispatchEvent(new Event(EVENT));
      if (syncBackend && user?.id) {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => saveToBackend(next), 400);
      }
      return next;
    });
  }, [syncBackend, user?.id]);

  const activeView = config.activeView || "daily";
  const view = config.views?.[activeView] || buildDefaultView(role, activeView);

  // 149-cn: eligible widgets untuk view aktif = intersect(views, permission).
  // Filter permission dilakukan di sini supaya widget yang tidak berhak TIDAK
  // muncul di dialog Atur Widget dan TIDAK di-mount oleh <Widget> wrapper.
  const eligibleWidgets = useMemo(() => {
    return DASHBOARD_WIDGETS.filter((w) =>
      w.views.includes(activeView) && hasWidgetPermission(w, canPerm, user)
    );
  }, [activeView, canPerm, user]);

  const isVisible = useCallback((id) => {
    const w = DASHBOARD_WIDGETS.find((x) => x.id === id);
    if (!w) return false;
    if (!w.views.includes(activeView)) return false;
    if (!hasWidgetPermission(w, canPerm, user)) return false;
    return !view.hidden[id];
  }, [view.hidden, activeView, canPerm, user]);

  const orderOf = useCallback((id) => {
    const idx = view.order.indexOf(id);
    return idx === -1 ? 999 : idx;
  }, [view.order]);

  const setActiveView = (v) => update((c) => ({ ...c, activeView: v }));
  const toggleVisibility = (id) => update((c) => {
    const cur = c.views[activeView];
    return { ...c, views: { ...c.views, [activeView]: { ...cur, hidden: { ...cur.hidden, [id]: !cur.hidden[id] } } } };
  });
  const moveTo = (id, targetIndex) => update((c) => {
    const cur = c.views[activeView];
    const arr = [...cur.order];
    const from = arr.indexOf(id);
    if (from === -1) return c;
    const clamped = Math.max(0, Math.min(targetIndex, arr.length - 1));
    if (from === clamped) return c;
    const [item] = arr.splice(from, 1);
    arr.splice(clamped, 0, item);
    return { ...c, views: { ...c.views, [activeView]: { ...cur, order: arr } } };
  });
  const moveUp = (id) => update((c) => {
    const cur = c.views[activeView];
    const arr = [...cur.order];
    const i = arr.indexOf(id);
    if (i > 0) [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]];
    return { ...c, views: { ...c.views, [activeView]: { ...cur, order: arr } } };
  });
  const moveDown = (id) => update((c) => {
    const cur = c.views[activeView];
    const arr = [...cur.order];
    const i = arr.indexOf(id);
    if (i >= 0 && i < arr.length - 1) [arr[i + 1], arr[i]] = [arr[i], arr[i + 1]];
    return { ...c, views: { ...c.views, [activeView]: { ...cur, order: arr } } };
  });
  const resetActiveView = () => update((c) => ({ ...c, views: { ...c.views, [activeView]: buildDefaultView(role, activeView) } }));
  const resetAll = () => update(() => defaultConfig(role));

  return {
    config, activeView, setActiveView, role,
    eligibleWidgets, isVisible, orderOf,
    toggleVisibility, moveUp, moveDown, moveTo,
    resetActiveView, reset: resetAll,
  };
}
